import { z } from "zod";
import { actionAvailabilitySchema, idSchema, instantSchema, moneySchema, reasonSchema } from "@amber/contracts";

export const HISTORY_ACTIONS = {
  extend: "collection.extend_history",
  controlExtension: "collection.control_extension",
  requestBackfill: "collection.request_backfill",
  cancelBackfill: "collection.cancel_backfill",
  gapDecision: "collection.gap_decision",
  pausePerson: "collection.pause_person",
  resumePerson: "collection.resume_person",
} as const;

export const OPERATOR_HISTORY_REASON =
  "Only Operators and Admins can extend history or pause, resume and cancel an extension";
export const OPERATOR_CANCEL_BACKFILL_REASON = "Only Operators and Admins can cancel a backfill";
export const EDITOR_GAP_REASON = "Only Editors and above can act on gaps";
export const EDITOR_PERSON_PAUSE_REASON = "Only Editors and above can pause or resume a person";

export const PAUSED_GAP_REASON = "Not scanned while paused";
export const RETIRED_GAP_REASON = "Not scanned while the person was retired";

const windowKeySchema = z.enum(["2_weeks", "4_months", "1_year", "2_years", "3_years", "5_years"]);

export const extensionPreviewRequestSchema = z.strictObject({ target: windowKeySchema });
export const extendHistoryRequestSchema = z.strictObject({ target: windowKeySchema, reason: reasonSchema });
export const jobControlRequestSchema = z.strictObject({
  reason: reasonSchema,
  base_revision: z.number().int().positive(),
});
export const requestBackfillSchema = z.strictObject({ reason: reasonSchema });
export const gapNextStepRequestSchema = z.strictObject({
  next_step: z.enum(["automatic_retry", "wait_for_source", "accept_as_permanent"]),
  note: z.string().trim().max(2000).default(""),
  reason: reasonSchema,
});
export const resolveGapRequestSchema = z.strictObject({
  note: z.string().trim().min(1, "Say how you know nothing is missing, or how it was collected").max(2000),
});
export const personPauseRequestSchema = z.strictObject({ reason: reasonSchema });
export const personResumeRequestSchema = z.strictObject({ reason: reasonSchema, catch_up: z.boolean().default(true) });

export const intervalEstimateSchema = z.strictObject({
  source_id: idSchema("src"),
  title: z.string(),
  people: z.array(z.string()),
  from: instantSchema.nullable(),
  to: instantSchema.nullable(),
  days: z.number().nonnegative(),
  items: z.number().nonnegative(),
  cost: moneySchema,
  storage_bytes: z.number().int().nonnegative(),
  unreachable: z.string().nullable(),
  skipped: z.string().nullable(),
});
export type IntervalEstimate = z.infer<typeof intervalEstimateSchema>;

export const extensionPreviewSchema = z.strictObject({
  target: windowKeySchema,
  target_label: z.string(),
  current: windowKeySchema,
  current_label: z.string(),
  queued_behind: z.string().nullable(),
  items: z.number().nonnegative(),
  cost: moneySchema,
  budget_share: z.number().nonnegative().nullable(),
  storage_bytes: z.number().int().nonnegative(),
  sources: z.array(intervalEstimateSchema),
  unreachable: z.array(z.string()),
  not_following: z.array(z.string()),
  note: z.string(),
});
export type ExtensionPreview = z.infer<typeof extensionPreviewSchema>;

export const BACKFILL_JOB_STATE_LABELS = {
  queued: "Queued",
  running: "Running",
  paused: "Paused",
  complete: "Complete",
  cancelled: "Cancelled",
} as const;

export const backfillJobViewSchema = z.strictObject({
  id: z.string(),
  kind: z.enum(["history_extension", "gap_backfill", "catch_up"]),
  state: z.enum(["queued", "running", "paused", "complete", "cancelled"]),
  state_label: z.string(),
  target: windowKeySchema.nullable(),
  target_label: z.string().nullable(),
  previous_label: z.string().nullable(),
  started_by: z.string(),
  created_at: instantSchema,
  started_at: instantSchema.nullable(),
  reason: z.string(),
  sources_total: z.number().int().nonnegative(),
  sources_complete: z.number().int().nonnegative(),
  progress: z.number().min(0).max(1),
  days_left: z.number().nonnegative().nullable(),
  waiting: z.array(z.strictObject({ source: z.string(), reason: z.string() })),
  last_change: z.strictObject({ at: instantSchema, by: z.string(), reason: z.string(), action: z.string() }).nullable(),
  revision: z.number().int().positive(),
});
export type BackfillJobView = z.infer<typeof backfillJobViewSchema>;

export const historyWindowViewSchema = z.strictObject({
  window: windowKeySchema,
  window_label: z.string(),
  changed: z.strictObject({ at: instantSchema, by: z.string() }).nullable(),
  extension: backfillJobViewSchema.nullable(),
  queued: backfillJobViewSchema.nullable(),
  recent: z.array(backfillJobViewSchema),
  people: z.array(
    z.strictObject({
      id: idSchema("prf"),
      name: z.string(),
      option_label: z.string(),
      custom: z.boolean(),
      pending: z.boolean(),
      back_to: z.iso.date().nullable(),
      complete_back_to: instantSchema.nullable(),
      progress: z
        .strictObject({ complete: z.number().int().nonnegative(), total: z.number().int().nonnegative() })
        .nullable(),
      note: z.string().nullable(),
    }),
  ),
  targets: z.array(z.strictObject({ key: windowKeySchema, label: z.string() })),
  actions: z.strictObject({ extend: actionAvailabilitySchema, control: actionAvailabilitySchema }),
});
export type HistoryWindowView = z.infer<typeof historyWindowViewSchema>;

export const backfillPreviewSchema = z.strictObject({
  gap_id: idSchema("gap"),
  estimate: intervalEstimateSchema,
  caps: z.string(),
  waits: z.string().nullable(),
});
export type BackfillPreview = z.infer<typeof backfillPreviewSchema>;

export const extensionStripSchema = z.strictObject({ text: z.string(), href: z.string() }).nullable();
